import * as THREE from "three";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { InstanceBatch } from "./instance_batch.js";
import { Vfx3DGlobalAtlas } from "../textures/vfx_3d_global_atlas.js";
import { AttributeSize } from "../types/core_types.js";

export const DissolvingSphereLayout = {
  aPosStartTime:   AttributeSize.Vec4, // xyz: start_pos, w: start_time
  aScaleOffsetDur: AttributeSize.Vec4, // x: start_scale, y: end_scale, z: initial_offset, w: duration
  aFlowDirSpeed:   AttributeSize.Vec4, // xyz: flow_direction (normalized), w: flow_speed
  aDissolveParams: AttributeSize.Vec4, // x: dissolve_start_value, y: dissolve_end_value, z: dissolve_start_progress, w: dissolve_softness
  aColor1:         AttributeSize.Vec4, // rgb: color_1, w: fire_end_ratio
  aColor2:         AttributeSize.Vec4, // rgb: color_2, w: rim_intensity
  aNoiseUv:        AttributeSize.Vec4, // min_x, min_y, max_x, max_y
} as const;

export class VfxDissolvingSphereBatch extends InstanceBatch<typeof DissolvingSphereLayout>
{
  private static readonly MAX_INSTANCES: number = 20;
  private static readonly RENDER_ORDER: number  = 21;

  // --- Замовчувані параметри руху та візуалу ---
  public static readonly DEFAULT_FLOW_DIRECTION: THREE.Vector3 = new THREE.Vector3( 0, 1, 0 ); // Рух текстури вгору
  public static readonly DEFAULT_FLOW_SPEED: number            = 1.0;

  private static readonly DEFAULT_COLOR_1: THREE.Color = new THREE.Color( 0x64DCFF ); // Яскравий старт
  private static readonly DEFAULT_COLOR_2: THREE.Color = new THREE.Color( 0x1E64FF ); // Насичений фініш
  private static readonly DEFAULT_DISSOLVE_SOFTNESS: number = 0.2;
  private static readonly DEFAULT_FIRE_END_RATIO: number    = 0.6;
  private static readonly DEFAULT_RIM_INTENSITY: number     = 0.35;

  private static cached_geometry: THREE.BufferGeometry | null = null;

  public constructor()
  {
    const base_geometry = VfxDissolvingSphereBatch.getSphereGeometry();

    let atlas_texture: THREE.Texture | null = null;
    try
    {
      atlas_texture = Vfx3DGlobalAtlas.getTexture();
    }
    catch ( _e )
    {
      // Atlas will be populated lazily in spawnSphere if called before init
    }

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uNoiseTexture: { value: atlas_texture },
        uTime:         { value: 0.0 },
      },
      vertexShader: `
        uniform float uTime;

        attribute vec4 aPosStartTime;
        attribute vec4 aScaleOffsetDur;
        attribute vec4 aFlowDirSpeed;
        attribute vec4 aDissolveParams;
        attribute vec4 aColor1;
        attribute vec4 aColor2;
        attribute vec4 aNoiseUv;

        varying vec3 vLocalPos;
        varying vec3 vFlowOffset;
        varying vec3 vNormal;
        varying vec3 vViewNormal;
        varying float vProgress;
        varying vec4 vDissolveParams;
        varying vec4 vColor1;
        varying vec4 vColor2;
        varying vec4 vNoiseUv;

        void main() {
          float start_time = aPosStartTime.w;
          float duration = aScaleOffsetDur.w;
          float age = max( 0.0, uTime - start_time );
          float progress = duration > 0.0 ? clamp( age / duration, 0.0, 1.0 ) : 0.0;
          vProgress = progress;
          vDissolveParams = aDissolveParams;
          vColor1 = aColor1;
          vColor2 = aColor2;
          vNoiseUv = aNoiseUv;

          // Плавне розширення сфери (Ease-out)
          float scale_progress = 1.0 - pow( 1.0 - progress, 2.2 );
          float current_scale = mix( aScaleOffsetDur.x, aScaleOffsetDur.y, scale_progress );

          // Лінійний 3D-рух текстури шуму у вказаному напрямку
          vec3 flow_dir = length( aFlowDirSpeed.xyz ) > 0.001 ? normalize( aFlowDirSpeed.xyz ) : vec3( 0.0, 1.0, 0.0 );
          float flow_speed = aFlowDirSpeed.w;
          vFlowOffset = flow_dir * ( flow_speed * age );
          vLocalPos = ( position * 0.5 + 0.5 ) + vec3( aScaleOffsetDur.z );

          vNormal = normal;
          vViewNormal = normalize( ( modelViewMatrix * vec4( normal, 0.0 ) ).xyz );

          vec4 mv_position = modelViewMatrix * vec4( aPosStartTime.xyz, 1.0 );
          mv_position.xyz += position * current_scale;
          gl_Position = projectionMatrix * mv_position;
        }
      `,
      fragmentShader: `
        uniform sampler2D uNoiseTexture;

        varying vec3 vLocalPos;
        varying vec3 vFlowOffset;
        varying vec3 vNormal;
        varying vec3 vViewNormal;
        varying float vProgress;
        varying vec4 vDissolveParams;
        varying vec4 vColor1;
        varying vec4 vColor2;
        varying vec4 vNoiseUv;

        void main() {
          // 1. Попіксельний розрахунок ваг Triplanar (n^4)
          vec3 n = abs( normalize( vNormal ) );
          vec3 blend = n * n;
          blend *= blend;
          blend /= ( blend.x + blend.y + blend.z );

          // 2. Узгоджений рух для всіх трьох проекційних площин
          // Площина X (боки): горизонталь Z, вертикаль Y
          vec2 norm_pos_x = fract( vec2( vLocalPos.z - vFlowOffset.z, vLocalPos.y - vFlowOffset.y ) );

          // Площина Z (перед/зад): горизонталь X, вертикаль Y
          vec2 norm_pos_z = fract( vec2( vLocalPos.x - vFlowOffset.x, vLocalPos.y - vFlowOffset.y ) );

          // Площина Y (полюси): горизонталь X, вертикаль Z (перекочується через купол разом із вертикальним потоком)
          float pole_dir = vNormal.y >= 0.0 ? 1.0 : -1.0;
          float flow_y_on_pole = vFlowOffset.y * pole_dir;
          vec2 norm_pos_y = fract( vec2( vLocalPos.x - vFlowOffset.x, vLocalPos.z - ( vFlowOffset.z + flow_y_on_pole ) ) );

          vec2 uv_x = mix( vNoiseUv.xy, vNoiseUv.zw, norm_pos_x );
          vec2 uv_y = mix( vNoiseUv.xy, vNoiseUv.zw, norm_pos_y );
          vec2 uv_z = mix( vNoiseUv.xy, vNoiseUv.zw, norm_pos_z );

          float sample_x = texture2D( uNoiseTexture, uv_x ).r;
          float sample_y = texture2D( uNoiseTexture, uv_y ).r;
          float sample_z = texture2D( uNoiseTexture, uv_z ).r;

          float noise = sample_x * blend.x + sample_y * blend.y + sample_z * blend.z;

          // 3. Розчинення за шумом
          float softness = max( vDissolveParams.w, 0.001 );
          float start_threshold = mix( -softness, 1.0 + softness, vDissolveParams.x );
          float end_threshold   = mix( -softness, 1.0 + softness, vDissolveParams.y );
          float dissolve_t      = clamp( ( vProgress - vDissolveParams.z ) / max( 1.0 - vDissolveParams.z, 0.0001 ), 0.0, 1.0 );
          float dissolve_threshold = mix( start_threshold, end_threshold, dissolve_t );
          float dissolve_alpha = smoothstep( dissolve_threshold - softness, dissolve_threshold + softness, noise );

          // 4. Колірний перехід
          float fire_end_ratio = max( vColor1.w, 0.001 );
          float fire_decay = clamp( vProgress / fire_end_ratio, 0.0, 1.0 );
          float fire_factor = clamp( noise * 1.25 - fire_decay * 1.5, 0.0, 1.0 );

          vec3 fire_color = mix( vColor2.rgb, vColor1.rgb, smoothstep( 0.0, 0.5, fire_factor ) );
          vec3 final_color = fire_color;

          // М'який силуетний блік сфери (Fresnel)
          float fresnel = pow( 1.0 - abs( normalize( vViewNormal ).z ), 2.5 );
          float rim_intensity = vColor2.w > 0.0 ? vColor2.w : 0.35;
          final_color += fire_color * fresnel * rim_intensity;

          // Спалах по контуру розчинення
          float rim_mask = smoothstep( 0.0, 0.5, dissolve_alpha ) * ( 1.0 - smoothstep( 0.5, 1.0, dissolve_alpha ) );
          final_color += vColor1.rgb * ( rim_mask * 0.4 * fire_factor );

          // 5. Плавне згасання
          float fade_out = 1.0 - smoothstep( 0.7, 1.0, vProgress );
          float alpha = dissolve_alpha * fade_out;

          gl_FragColor = vec4( final_color, alpha );
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.FrontSide,
    } );

    super( { max_instances: VfxDissolvingSphereBatch.MAX_INSTANCES, layout: DissolvingSphereLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = VfxDissolvingSphereBatch.RENDER_ORDER;
  }

  private static getSphereGeometry(): THREE.BufferGeometry
  {
    if ( VfxDissolvingSphereBatch.cached_geometry )
      return VfxDissolvingSphereBatch.cached_geometry;

    const geom = new THREE.IcosahedronGeometry( 1.0, 2 );
    VfxDissolvingSphereBatch.cached_geometry = geom;
    return geom;
  }

  public spawnSphere(
    noise_resource_id: ResourceID,
    start_pos: THREE.Vector3,
    start_scale: number,
    end_scale: number,
    start_time: number,
    duration: number,
    dissolve_start_value: number = 0.0,
    dissolve_end_value: number = 1.0,
    dissolve_start_progress: number = 0.0,
    flow_direction?: THREE.Vector3,
    flow_speed?: number,
    initial_offset?: number,
    color_1?: THREE.Color,
    color_2?: THREE.Color,
    dissolve_softness: number = VfxDissolvingSphereBatch.DEFAULT_DISSOLVE_SOFTNESS,
    fire_end_ratio: number = VfxDissolvingSphereBatch.DEFAULT_FIRE_END_RATIO,
    rim_intensity: number = VfxDissolvingSphereBatch.DEFAULT_RIM_INTENSITY
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !mat.uniforms.uNoiseTexture.value )
    {
      try
      {
        mat.uniforms.uNoiseTexture.value = Vfx3DGlobalAtlas.getTexture();
      }
      catch ( _e )
      {
        // Atlas not yet ready
      }
    }

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    const dir = flow_direction ?? VfxDissolvingSphereBatch.DEFAULT_FLOW_DIRECTION;
    let dx = 0, dy = 1, dz = 0;
    if ( dir && dir.lengthSq() > 0.0001 )
    {
      const len = dir.length();
      dx = dir.x / len;
      dy = dir.y / len;
      dz = dir.z / len;
    }

    const speed = flow_speed ?? VfxDissolvingSphereBatch.DEFAULT_FLOW_SPEED;
    const offset = initial_offset ?? Math.random();

    const c1 = color_1 ?? VfxDissolvingSphereBatch.DEFAULT_COLOR_1;
    const c2 = color_2 ?? VfxDissolvingSphereBatch.DEFAULT_COLOR_2;

    let noise_uv = { min_x: 0, min_y: 0, max_x: 1, max_y: 1 };
    try
    {
      noise_uv = Vfx3DGlobalAtlas.getUVRect( noise_resource_id );
    }
    catch ( _e ) {}

    const pos_start_attr        = this.geometry.getAttribute( "aPosStartTime" ).array as Float32Array;
    const scale_offset_dur_attr = this.geometry.getAttribute( "aScaleOffsetDur" ).array as Float32Array;
    const flow_dir_speed_attr   = this.geometry.getAttribute( "aFlowDirSpeed" ).array as Float32Array;
    const dissolve_params_attr  = this.geometry.getAttribute( "aDissolveParams" ).array as Float32Array;
    const color_1_attr          = this.geometry.getAttribute( "aColor1" ).array as Float32Array;
    const color_2_attr          = this.geometry.getAttribute( "aColor2" ).array as Float32Array;
    const noise_uv_attr         = this.geometry.getAttribute( "aNoiseUv" ).array as Float32Array;

    const o4 = idx * 4;

    pos_start_attr[ o4 ]     = start_pos.x;
    pos_start_attr[ o4 + 1 ] = start_pos.y;
    pos_start_attr[ o4 + 2 ] = start_pos.z;
    pos_start_attr[ o4 + 3 ] = start_time;

    scale_offset_dur_attr[ o4 ]     = start_scale;
    scale_offset_dur_attr[ o4 + 1 ] = end_scale;
    scale_offset_dur_attr[ o4 + 2 ] = offset;
    scale_offset_dur_attr[ o4 + 3 ] = duration;

    flow_dir_speed_attr[ o4 ]     = dx;
    flow_dir_speed_attr[ o4 + 1 ] = dy;
    flow_dir_speed_attr[ o4 + 2 ] = dz;
    flow_dir_speed_attr[ o4 + 3 ] = speed;

    dissolve_params_attr[ o4 ]     = dissolve_start_value;
    dissolve_params_attr[ o4 + 1 ] = dissolve_end_value;
    dissolve_params_attr[ o4 + 2 ] = dissolve_start_progress;
    dissolve_params_attr[ o4 + 3 ] = dissolve_softness;

    color_1_attr[ o4 ]     = c1.r;
    color_1_attr[ o4 + 1 ] = c1.g;
    color_1_attr[ o4 + 2 ] = c1.b;
    color_1_attr[ o4 + 3 ] = fire_end_ratio;

    color_2_attr[ o4 ]     = c2.r;
    color_2_attr[ o4 + 1 ] = c2.g;
    color_2_attr[ o4 + 2 ] = c2.b;
    color_2_attr[ o4 + 3 ] = rim_intensity;

    noise_uv_attr[ o4 ]     = noise_uv.min_x;
    noise_uv_attr[ o4 + 1 ] = noise_uv.min_y;
    noise_uv_attr[ o4 + 2 ] = noise_uv.max_x;
    noise_uv_attr[ o4 + 3 ] = noise_uv.max_y;

    this.geometry.getAttribute( "aPosStartTime" ).needsUpdate   = true;
    this.geometry.getAttribute( "aScaleOffsetDur" ).needsUpdate = true;
    this.geometry.getAttribute( "aFlowDirSpeed" ).needsUpdate   = true;
    this.geometry.getAttribute( "aDissolveParams" ).needsUpdate = true;
    this.geometry.getAttribute( "aColor1" ).needsUpdate         = true;
    this.geometry.getAttribute( "aColor2" ).needsUpdate         = true;
    this.geometry.getAttribute( "aNoiseUv" ).needsUpdate        = true;

    return this.getStableId( idx );
  }

  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}
